/**
 * Shared month-range + CSV helpers for the Finance dashboard and reports
 * (FIN-13). Kept separate from `finance-format.ts` (display/enum helpers) so the
 * reporting concerns stay co-located and easy to find.
 *
 * Month keys are always `YYYY-MM`. The analytics endpoints take an inclusive
 * `[from, to]` month range and the query hooks stay disabled until both are
 * syntactically valid, so these helpers only ever produce well-formed keys.
 */

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Current month as `YYYY-MM` (local time). */
export function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Shift a `YYYY-MM` key by `delta` months (may be negative). */
export function addMonths(ym: string, delta: number): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Previous month of a `YYYY-MM` key. */
export function prevMonth(ym: string): string {
  return addMonths(ym, -1);
}

export function isValidMonth(ym: string): boolean {
  return MONTH_RE.test(ym);
}

/** Inclusive `YYYY-MM` keys from `from` to `to` (empty if inverted/invalid). */
export function monthRange(from: string, to: string): string[] {
  if (!isValidMonth(from) || !isValidMonth(to)) return [];
  const out: string[] = [];
  let cur = from;
  // Guard against an inverted or pathological range.
  for (let i = 0; i < 600 && cur <= to; i += 1) {
    out.push(cur);
    cur = addMonths(cur, 1);
  }
  return out;
}

/** `"2026-03"` → `"March 2026"` (full label for headings / nav). */
export function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  return new Date(y, m - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

// ─── Date-range presets (shared by the Reports hub) ───────────────────────────

export type RangePreset = 'this-year' | 'last-12' | 'last-year';

/** Resolve a preset to a `[from, to]` month range relative to the current month. */
export function presetRange(preset: RangePreset): { from: string; to: string } {
  const now = currentMonth();
  const [y] = now.split('-').map(Number);
  switch (preset) {
    case 'this-year':
      return { from: `${y}-01`, to: `${y}-12` };
    case 'last-year':
      return { from: `${y - 1}-01`, to: `${y - 1}-12` };
    case 'last-12':
    default:
      return { from: addMonths(now, -11), to: now };
  }
}

// ─── Dashboard range presets (month-keyed, drive KPIs/trend/candlestick) ──────

export type DashboardRangePreset =
  | 'this-month'
  | 'last-month'
  | 'last-3-months'
  | 'last-12-months'
  | 'custom';

export const DASHBOARD_PRESET_LABELS: Record<DashboardRangePreset, string> = {
  'this-month': 'This month',
  'last-month': 'Last month',
  'last-3-months': 'Last 3 months',
  'last-12-months': 'Last 12 months',
  custom: 'Custom',
};

/**
 * Resolve a dashboard preset to a `{ fromMonth, toMonth }` (YYYY-MM) range
 * relative to the current month. `custom` is resolved by the caller (it keeps
 * its own from/to state), so we default it to the last-12-months window.
 */
export function dashboardPresetRange(preset: DashboardRangePreset): {
  fromMonth: string;
  toMonth: string;
} {
  const now = currentMonth();
  switch (preset) {
    case 'this-month':
      return { fromMonth: now, toMonth: now };
    case 'last-month': {
      const prev = prevMonth(now);
      return { fromMonth: prev, toMonth: prev };
    }
    case 'last-3-months':
      return { fromMonth: addMonths(now, -2), toMonth: now };
    case 'last-12-months':
    case 'custom':
    default:
      return { fromMonth: addMonths(now, -11), toMonth: now };
  }
}

/** A human label for a month range, e.g. "Last 12 Months" / "This Month". */
export function dashboardRangeWord(preset: DashboardRangePreset): string {
  switch (preset) {
    case 'this-month':
      return 'This Month';
    case 'last-month':
      return 'Last Month';
    case 'last-3-months':
      return 'Last 3 Months';
    case 'last-12-months':
      return 'Last 12 Months';
    case 'custom':
    default:
      return 'Selected Range';
  }
}

/** First day of a `YYYY-MM` month as an ISO date `YYYY-MM-DD`. */
export function monthStartIso(ym: string): string {
  return `${ym}-01`;
}

/** Last day of a `YYYY-MM` month as an ISO date `YYYY-MM-DD`. */
export function monthEndIso(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  // Day 0 of the next month is the last day of this month.
  const d = new Date(y, m, 0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

// ─── CSV export ───────────────────────────────────────────────────────────────

/** Escape one CSV field (RFC 4180): quote when it contains a comma, quote or
 *  newline; double embedded quotes. */
function csvField(value: unknown): string {
  const s = value == null ? '' : String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/**
 * Serialize a header row + data rows to a CSV string. Rows are arrays of
 * primitive cells aligned to `headers`. Uses CRLF line endings so the file
 * opens cleanly in Excel as well as Sheets/Numbers.
 */
export function toCsv(headers: string[], rows: Array<Array<string | number>>): string {
  const lines = [headers.map(csvField).join(',')];
  for (const row of rows) lines.push(row.map(csvField).join(','));
  return lines.join('\r\n');
}

/** Trigger a client-side download of `content` as `filename`. */
export function downloadCsv(filename: string, content: string): void {
  // Prepend a UTF-8 BOM so Excel detects the encoding (handles € correctly).
  const blob = new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
